"use client"

import { AlertCircle } from "lucide-react"
import { useTranslations } from "next-intl"

import PageChangeTransition from "@/components/shared/PageChangeTransition"
import SpectraLogo from "@/components/shared/SpectraLogo"
import { Button } from "@/components/ui/button"
import { useEffect } from "react"
import * as Sentry from "@sentry/nextjs"
import { useRouter } from "next/navigation"

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const t = useTranslations("common")
  const router = useRouter()

  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  const handleTryAgain = async () => {
    // Clear all cookies
    const cookies = document.cookie.split(";")
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf("=")
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    }
    router.refresh()
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <PageChangeTransition>
          <div className="p-8">
            <div className="flex items-center justify-center">
              <SpectraLogo className="text-3xl" />
            </div>
            <div className="mt-6 text-center">
              <AlertCircle className="mx-auto mb-4 size-12 text-destructive" />
              <h2 className="text-xl font-semibold text-gray-900">{t("global-error.title")}</h2>
              <p className="mt-2 text-sm text-gray-600">{t("global-error.description")}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-8 py-4">
            <Button onClick={handleTryAgain} variant="default" className="w-full">
              {t("global-error.try-again")}
            </Button>
          </div>
        </PageChangeTransition>
      </div>
    </div>
  )
}
